import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
    Box,
    Typography,
    Container,
    Paper,
    Button,
    CircularProgress,
    Stack,
    Alert,
    Divider
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import Grid from '@mui/material/GridLegacy';
import { ArrowLeft, Activity, Bell } from 'lucide-react';
import ParentNavbar from '../student/ParentNavbar';
import { getChildUsageSummary } from '../../services/parentService';
import { UsageDaySnapshot } from '../../services/usageService';
import { Bar } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Tooltip,
    Legend,
    ChartData,
    ChartOptions
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const ParentStudentDetail: React.FC = () => {
    const { childId } = useParams<{ childId: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();

    const [studentName, setStudentName] = useState<string>(location.state?.studentName || 'Student');
    const [isUsageLoading, setIsUsageLoading] = useState(true);
    const [usageDays, setUsageDays] = useState<UsageDaySnapshot[]>([]);
    const [usageStats, setUsageStats] = useState({ totalMinutes: 0, activeDays: 0, streakDays: 0 });
    const [usageError, setUsageError] = useState<string | null>(null);

    useEffect(() => {
        if (!childId) return;

        const fetchUsage = async () => {
            setIsUsageLoading(true);
            setUsageError(null);
            try {
                const data = await getChildUsageSummary(childId, 7);
                setUsageDays(data.days);
                setUsageStats({
                    totalMinutes: data.totalMinutes,
                    activeDays: data.activeDays,
                    streakDays: data.streakDays,
                });
            } catch (error) {
                console.error('Failed to load child usage', error);
                setUsageError('Unable to load usage data. Please try again later.');
            } finally {
                setIsUsageLoading(false);
            }
        };

        fetchUsage();
    }, [childId]);

    const formatMinutesAsDuration = (minutes: number) => {
        if (minutes <= 0) return '0 min';
        const hours = Math.floor(minutes / 60);
        const remainingMinutes = Math.round(minutes % 60);
        if (hours === 0) return `${remainingMinutes} min`;
        if (remainingMinutes === 0) return `${hours}h`;
        return `${hours}h ${remainingMinutes}m`;
    };

    const usageChartData: ChartData<'bar'> = {
        labels: usageDays.map(day => new Intl.DateTimeFormat(undefined, { weekday: 'short', month: 'short', day: 'numeric' }).format(new Date(day.date))),
        datasets: [{
            label: 'Study Hours',
            data: usageDays.map(day => Number((day.totalMinutes / 60).toFixed(2))),
            backgroundColor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.55 : 0.65),
            borderColor: theme.palette.primary.main,
            borderWidth: 1.5,
            borderRadius: 6,
        }],
    };

    const usageChartOptions: ChartOptions<'bar'> = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            tooltip: {
                backgroundColor: theme.palette.mode === 'dark' ? '#0f172a' : '#1e293b',
                titleColor: '#ffffff',
                bodyColor: '#ffffff',
                callbacks: { label: (context) => `Study Hours: ${(context.parsed.y || 0).toFixed(2)}h` }
            }
        },
        scales: {
            y: {
                beginAtZero: true,
                grid: { color: alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.16 : 0.08) },
                ticks: {
                    color: theme.palette.text.secondary,
                    callback: value => `${value}h`
                }
            },
            x: {
                grid: { display: false },
                ticks: { color: theme.palette.text.secondary }
            }
        }
    };

    return (
        <Box sx={(theme) => ({ minHeight: '100vh', backgroundColor: theme.palette.background.default })}>
            <ParentNavbar />

            <Container maxWidth="lg" sx={{ mt: 12, mb: 4 }}>
                <Button
                    startIcon={<ArrowLeft size={18} />}
                    onClick={() => navigate('/parent-dashboard')}
                    sx={{ mb: 3, color: 'text.secondary', textTransform: 'none', fontWeight: 600 }}
                >
                    Back to Dashboard
                </Button>

                <Box sx={{ mb: 4 }}>
                    <Typography variant="h4" fontWeight="bold" sx={{ color: 'text.primary', mb: 1 }}>
                        {studentName}'s Digital Wellbeing
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        Review recent platform activity and study habits.
                    </Typography>
                </Box>

                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Paper sx={(theme) => ({
                            p: 4,
                            borderRadius: 4,
                            backgroundColor: theme.palette.background.paper,
                            border: `1px solid ${theme.palette.divider}`,
                            boxShadow: theme.palette.mode === 'dark'
                                ? '0 14px 40px rgba(0, 0, 0, 0.35)'
                                : '0 4px 20px rgba(0,0,0,0.05)'
                        })}>
                            {usageError && <Alert severity="error" sx={{ mb: 3 }}>{usageError}</Alert>}

                            {isUsageLoading ? (
                                <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
                                    <CircularProgress size={40} sx={{ color: '#6c5ce7' }} />
                                </Box>
                            ) : usageDays.length > 0 && usageDays.some(day => day.totalMinutes > 0) ? (
                                <>
                                    <Box sx={{ height: 350, mb: 4 }}>
                                        <Bar data={usageChartData} options={usageChartOptions} />
                                    </Box>

                                    <Divider sx={(theme) => ({ my: 4, borderColor: theme.palette.divider })} />

                                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={4} justifyContent="space-around">
                                        <Box sx={{ textAlign: 'center' }}>
                                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                                Total Time (Last 7 Days)
                                            </Typography>
                                            <Typography variant="h4" fontWeight={700} color="text.primary">
                                                {formatMinutesAsDuration(usageStats.totalMinutes)}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ textAlign: 'center' }}>
                                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                                Active Days
                                            </Typography>
                                            <Typography variant="h4" fontWeight={700} color="text.primary">
                                                {usageStats.activeDays}/7
                                            </Typography>
                                        </Box>
                                        <Box sx={{ textAlign: 'center' }}>
                                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                                Studying Streak
                                            </Typography>
                                            <Typography variant="h4" fontWeight={700} color="text.primary">
                                                {usageStats.streakDays} day{usageStats.streakDays !== 1 ? 's' : ''}
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </>
                            ) : (
                                <Box sx={{ py: 8, textAlign: 'center' }}>
                                    <Box sx={(theme) => ({
                                        width: 80, height: 80, borderRadius: '50%', backgroundColor: theme.palette.mode === 'dark'
                                            ? alpha(theme.palette.common.white, 0.08)
                                            : '#f5f6fa',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px'
                                    })}>
                                        <Activity size={40} color={theme.palette.text.disabled} />
                                    </Box>
                                    <Typography variant="h6" color="text.primary" gutterBottom>
                                        No Activity Yet
                                    </Typography>
                                    <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 400, mx: 'auto' }}>
                                        {studentName} hasn't been active on the platform in the last 7 days. Check back later for updates.
                                    </Typography>
                                </Box>
                            )}
                        </Paper>
                    </Grid>

                    {/* Scoreboard Card */}
                    <Grid item xs={12}>
                        <Paper
                            elevation={0}
                            onClick={() => navigate(`/parent/student/${childId}/scores`)}
                            sx={(theme) => ({
                                p: { xs: 2.5, sm: 3 },
                                borderRadius: 3,
                                border: `1px solid ${alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.45 : 0.2)}`,
                                background: theme.palette.mode === 'dark'
                                    ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.2)} 0%, ${alpha(theme.palette.background.paper, 0.95)} 100%)`
                                    : 'linear-gradient(135deg, #f3f0ff 0%, #faf8ff 100%)',
                                cursor: 'pointer',
                                display: 'flex',
                                flexDirection: { xs: 'column', sm: 'row' },
                                alignItems: { xs: 'flex-start', sm: 'center' },
                                justifyContent: 'space-between',
                                gap: { xs: 2, sm: 0 },
                                transition: 'all 0.25s ease',
                                '&:hover': {
                                    borderColor: theme.palette.primary.main,
                                    boxShadow: theme.palette.mode === 'dark'
                                        ? '0 8px 24px rgba(15, 23, 42, 0.5)'
                                        : '0 8px 24px rgba(108, 92, 231, 0.18)',
                                    transform: 'translateY(-2px)',
                                    background: theme.palette.mode === 'dark'
                                        ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.28)} 0%, ${alpha(theme.palette.background.paper, 1)} 100%)`
                                        : 'linear-gradient(135deg, #ece9ff 0%, #f5f2ff 100%)',
                                }
                            })}
                        >
                            {/* Left: icon + text */}
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                                <Box
                                    sx={{
                                        width: 48,
                                        height: 48,
                                        borderRadius: '12px',
                                        background: 'linear-gradient(135deg, #6c5ce7, #a29bfe)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '1.4rem',
                                        flexShrink: 0,
                                        boxShadow: '0 4px 12px rgba(108, 92, 231, 0.35)',
                                    }}
                                >
                                    🏆
                                </Box>
                                <Box>
                                    <Typography
                                        variant="subtitle1"
                                        sx={{ fontWeight: 700, color: 'text.primary', lineHeight: 1.3 }}
                                    >
                                        Mock Test Scoreboard
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.2 }}>
                                        View {studentName}&apos;s subject-wise quiz performance
                                    </Typography>
                                </Box>
                            </Box>

                            {/* Right: arrow */}
                            <Box
                                sx={(theme) => ({
                                    width: 36,
                                    height: 36,
                                    borderRadius: '50%',
                                    background: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.3 : 0.12),
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                    alignSelf: { xs: 'flex-end', sm: 'center' },
                                })}
                            >
                                <Typography sx={{ color: 'primary.main', fontWeight: 700, fontSize: '1.1rem' }}>
                                    →
                                </Typography>
                            </Box>
                        </Paper>
                    </Grid>

                    {/* Activity Notifications Card */}
                    <Grid item xs={12}>
                        <Paper
                            elevation={0}
                            onClick={() => navigate(`/parent/student/${childId}/notifications`, { state: { studentName } })}
                            sx={(theme) => ({
                                p: { xs: 2.5, sm: 3 },
                                borderRadius: 3,
                                border: `1px solid ${alpha(theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.4 : 0.2)}`,
                                background: theme.palette.mode === 'dark'
                                    ? `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.2)} 0%, ${alpha(theme.palette.background.paper, 0.95)} 100%)`
                                    : 'linear-gradient(135deg, #fffcf9 0%, #fff7f0 100%)',
                                cursor: 'pointer',
                                display: 'flex',
                                flexDirection: { xs: 'column', sm: 'row' },
                                alignItems: { xs: 'flex-start', sm: 'center' },
                                justifyContent: 'space-between',
                                gap: { xs: 2, sm: 0 },
                                transition: 'all 0.25s ease',
                                '&:hover': {
                                    borderColor: theme.palette.warning.main,
                                    boxShadow: theme.palette.mode === 'dark'
                                        ? '0 8px 24px rgba(15, 23, 42, 0.5)'
                                        : '0 8px 24px rgba(255, 159, 67, 0.15)',
                                    transform: 'translateY(-2px)',
                                    background: theme.palette.mode === 'dark'
                                        ? `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.28)} 0%, ${alpha(theme.palette.background.paper, 1)} 100%)`
                                        : 'linear-gradient(135deg, #fff7f0 0%, #ffe9d6 100%)',
                                }
                            })}
                        >
                            {/* Left: icon + text */}
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                                <Box
                                    sx={{
                                        width: 48,
                                        height: 48,
                                        borderRadius: '12px',
                                        background: 'linear-gradient(135deg, #ff9f43, #feca57)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: '#fff',
                                        flexShrink: 0,
                                        boxShadow: '0 4px 12px rgba(255, 159, 67, 0.35)',
                                    }}
                                >
                                    <Bell size={24} />
                                </Box>
                                <Box>
                                    <Typography
                                        variant="subtitle1"
                                        sx={{ fontWeight: 700, color: 'text.primary', lineHeight: 1.3 }}
                                    >
                                        Activity Notifications
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.2 }}>
                                        View recent alerts and messages for {studentName}
                                    </Typography>
                                </Box>
                            </Box>

                            {/* Right: toggle icon */}
                            <Box
                                sx={(theme) => ({
                                    width: 36,
                                    height: 36,
                                    borderRadius: '50%',
                                    background: alpha(theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.32 : 0.12),
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                    alignSelf: { xs: 'flex-end', sm: 'center' },
                                })}
                            >
                                <Typography sx={{ color: 'warning.main', fontWeight: 700, fontSize: '1.1rem' }}>
                                    →
                                </Typography>
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>
            </Container>
        </Box>
    );
};

export default ParentStudentDetail;
